<template>
	<div>
		<PageHeader
			:data="{
				name: 'AR Collections',
				breadcrumbs: [{ name: 'Home', to: '/' }],
			}"
		/>

		<!-- Summary Cards -->
		<div class="row mb-3">
			<div class="col-md-3">
				<div class="card">
					<div class="card-body text-center">
						<h6 class="text-muted mb-1">Active Escalations</h6>
						<h3 class="mb-0">{{ stats.active }}</h3>
					</div>
				</div>
			</div>
			<div class="col-md-3">
				<div class="card">
					<div class="card-body text-center">
						<h6 class="text-muted mb-1">Paused</h6>
						<h3 class="mb-0 text-warning">{{ stats.paused }}</h3>
					</div>
				</div>
			</div>
			<div class="col-md-3">
				<div class="card">
					<div class="card-body text-center">
						<h6 class="text-muted mb-1">Admin Disabled</h6>
						<h3 class="mb-0 text-danger">{{ stats.adminDisabled }}</h3>
					</div>
				</div>
			</div>
			<div class="col-md-3">
				<div class="card">
					<div class="card-body text-center">
						<h6 class="text-muted mb-1">Total Outstanding</h6>
						<h3 class="mb-0">${{ stats.totalAmount }}</h3>
					</div>
				</div>
			</div>
		</div>

		<!-- Sandbox Banner -->
		<div v-if="arSettings.sandbox" class="alert alert-warning d-flex align-items-center mb-3">
			<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-2" />
			<strong>SANDBOX MODE</strong>&nbsp;- Emails go to {{ arSettings.sandbox_emails }} instead of real clients. No Plesk actions will fire.
			<button class="btn btn-sm btn-outline-dark ms-auto" @click="activeTab = 'settings'; loadArSettings()">
				Settings
			</button>
		</div>

		<!-- View Toggle -->
		<div class="mb-3">
			<div class="btn-group">
				<button class="btn btn-sm" :class="activeTab === 'escalations' ? 'btn-primary' : 'btn-outline-primary'" @click="activeTab = 'escalations'">
					<font-awesome-icon :icon="['fas', 'list']" /> Escalations
				</button>
				<button class="btn btn-sm" :class="activeTab === 'templates' ? 'btn-primary' : 'btn-outline-primary'" @click="activeTab = 'templates'; loadTemplates()">
					<font-awesome-icon :icon="['fas', 'envelope']" /> Email Templates
				</button>
				<button class="btn btn-sm" :class="activeTab === 'settings' ? 'btn-primary' : 'btn-outline-primary'" @click="activeTab = 'settings'; loadArSettings()">
					<font-awesome-icon :icon="['fas', 'cog']" /> Settings
				</button>
			</div>
		</div>

		<!-- ==================== ESCALATIONS TAB ==================== -->
		<template v-if="activeTab === 'escalations'">

		<!-- Filters -->
		<div class="card mb-3">
			<div class="card-body">
				<div class="row align-items-end">
					<div class="col-md-3">
						<label class="form-label">Search Client</label>
						<input type="text" class="form-control form-control-sm" v-model="search" placeholder="Client name..." @input="debounceSearch" />
					</div>
					<div class="col-md-2">
						<label class="form-label">Stage</label>
						<select class="form-select form-select-sm" v-model="stageFilter" @change="getTableData">
							<option value="">All Stages</option>
							<option value="pre_due">Pre-Due (-7)</option>
							<option value="due_day">Due Day</option>
							<option value="overdue_1">Overdue +1</option>
							<option value="overdue_5">Overdue +5</option>
							<option value="overdue_10">Overdue +10</option>
							<option value="overdue_20">Overdue +20</option>
						</select>
					</div>
					<div class="col-md-2">
						<label class="form-label">Status</label>
						<select class="form-select form-select-sm" v-model="statusFilter" @change="getTableData">
							<option value="active">Active Only</option>
							<option value="all">All</option>
							<option value="paused">Paused</option>
							<option value="completed">Completed</option>
						</select>
					</div>
					<div class="col-md-2">
						<button class="btn btn-outline-secondary btn-sm" @click="resetFilters">
							<font-awesome-icon :icon="['fas', 'times-circle']" /> Clear
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Table -->
		<div class="card">
			<div class="card-body">
				<div v-if="loading" class="d-flex justify-content-center py-5">
					<div class="spinner-border" role="status">
						<span class="visually-hidden">Loading...</span>
					</div>
				</div>

				<div v-else-if="tableData.length === 0" class="text-center py-5">
					<h5 class="text-muted">No escalations found</h5>
					<p class="text-muted">Run <code>php artisan ar:process-escalations</code> to create escalations from overdue invoices.</p>
				</div>

				<table v-else class="table table-hover">
					<thead>
						<tr>
							<th>Client</th>
							<th>Invoice</th>
							<th>Amount</th>
							<th>Due Date</th>
							<th>Days Overdue</th>
							<th>Stage</th>
							<th>Status</th>
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="esc in tableData" :key="esc.id" :class="rowClass(esc)">
							<td>
								<strong>{{ esc.client ? esc.client.name : 'N/A' }}</strong>
								<br /><small class="text-muted">ID: {{ esc.client_id }}</small>
							</td>
							<td>
								<span v-if="esc.triggering_invoice">#{{ esc.triggering_invoice.invoice_number }}</span>
								<span v-else class="text-muted">-</span>
							</td>
							<td>
								<span v-if="esc.triggering_invoice">${{ formatAmount(esc.triggering_invoice.amount) }}</span>
								<span v-else>-</span>
							</td>
							<td>
								<span v-if="esc.triggering_invoice">{{ formatDate(esc.triggering_invoice.due_date) }}</span>
							</td>
							<td>
								<span v-if="esc.triggering_invoice" :class="overdueBadgeClass(esc.triggering_invoice.due_date)">
									{{ daysOverdue(esc.triggering_invoice.due_date) }}
								</span>
							</td>
							<td>
								<span class="badge" :class="stageBadgeClass(esc.current_stage)">{{ stageLabel(esc.current_stage) }}</span>
							</td>
							<td>
								<span v-if="esc.completed_at" class="badge bg-success">Completed</span>
								<span v-else-if="esc.paused" class="badge bg-warning text-dark">
									Paused
									<br /><small>{{ esc.pause_reason }}</small>
								</span>
								<span v-else-if="esc.admin_disabled_at && !esc.admin_reactivated_at" class="badge bg-danger">Admin Disabled</span>
								<span v-else class="badge bg-primary">Active</span>
							</td>
							<td>
								<div class="d-flex gap-1">
									<button v-if="!esc.completed_at && !esc.paused" class="btn btn-warning btn-sm" @click="pauseEscalation(esc)" title="Pause">
										<font-awesome-icon :icon="['fas', 'pause']" /> Pause
									</button>
									<button v-if="esc.paused" class="btn btn-success btn-sm" @click="resumeEscalation(esc)" title="Resume">
										<font-awesome-icon :icon="['fas', 'play']" /> Resume
									</button>
									<button v-if="!esc.completed_at" class="btn btn-primary btn-sm" @click="confirmPayment(esc)" title="Confirm Payment">
										<font-awesome-icon :icon="['fas', 'dollar-sign']" /> Paid
									</button>
									<button v-if="!esc.completed_at && esc.current_stage === 'overdue_20' && !esc.site_disabled_at" class="btn btn-danger btn-sm" @click="disableSite(esc)" title="Disable Public Site">
										<font-awesome-icon :icon="['fas', 'lock']" /> Disable Site
									</button>
									<button class="btn btn-secondary btn-sm" @click="showDetails(esc)" title="View Details">
										<font-awesome-icon :icon="['fas', 'eye']" />
									</button>
								</div>
							</td>
						</tr>
					</tbody>
				</table>

				<!-- Pagination -->
				<nav v-if="pagination.last_page > 1" class="mt-3">
					<ul class="pagination pagination-sm justify-content-center">
						<li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
							<a class="page-link" href="#" @click.prevent="goToPage(pagination.current_page - 1)">Prev</a>
						</li>
						<li v-for="p in pagination.last_page" :key="p" class="page-item" :class="{ active: p === pagination.current_page }">
							<a class="page-link" href="#" @click.prevent="goToPage(p)">{{ p }}</a>
						</li>
						<li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
							<a class="page-link" href="#" @click.prevent="goToPage(pagination.current_page + 1)">Next</a>
						</li>
					</ul>
				</nav>
			</div>
		</div>

		</template>

		<!-- ==================== EMAIL TEMPLATES TAB ==================== -->
		<template v-if="activeTab === 'templates'">
		<div class="alert alert-info mb-3" role="alert">
			<font-awesome-icon :icon="['fas', 'info-circle']" class="me-1" />
			<strong>How email templates work:</strong> You edit only the <em>body text</em> here. The branded header (Town Web logo), the red <em>View/Pay Invoice Online</em> button, and the footer are managed centrally to keep all emails consistent. Use the variables below to insert dynamic content.
		</div>
		<div class="card mb-3">
			<div class="card-header d-flex justify-content-between align-items-center">
				<h6 class="mb-0">AR Collection Email Templates</h6>
				<small class="text-muted">Available variables: <code v-for="v in templateVariables" :key="v" class="me-1">{{ wrapVar(v) }}</code></small>
			</div>
			<div class="card-body">
				<div v-if="templatesLoading" class="d-flex justify-content-center py-3">
					<div class="spinner-border spinner-border-sm" role="status"></div>
				</div>
				<div v-else>
					<div v-for="tpl in emailTemplates" :key="tpl.id" class="border rounded p-3 mb-3">
						<div class="d-flex justify-content-between align-items-start mb-2">
							<div>
								<h6 class="mb-1">{{ tpl.name }}</h6>
								<small class="text-muted">{{ tpl.description }}</small>
							</div>
							<div>
								<span class="badge" :class="tpl.status === 'active' ? 'bg-success' : 'bg-secondary'">{{ tpl.status }}</span>
								<button class="btn btn-outline-primary btn-sm ms-2" @click="editTemplate(tpl)">
									<font-awesome-icon :icon="['fas', 'edit']" /> Edit
								</button>
							</div>
						</div>
						<div class="row">
							<div class="col-md-4">
								<small class="text-muted">Subject:</small><br>
								<span>{{ tpl.subject }}</span>
							</div>
							<div class="col-md-8">
								<small class="text-muted">Preview:</small><br>
								<div class="border rounded p-2 bg-light" style="max-height: 120px; overflow-y: auto; font-size: 13px;" v-html="truncateHtml(tpl.html_content)"></div>
							</div>
						</div>
					</div>
					<div v-if="emailTemplates.length === 0" class="text-center py-3">
						<p class="text-muted">No AR email templates found. Run the migration to seed them.</p>
					</div>
				</div>
			</div>
		</div>
		</template>

		<!-- ==================== SETTINGS TAB ==================== -->
		<template v-if="activeTab === 'settings'">
		<div class="card mb-3">
			<div class="card-header">
				<h6 class="mb-0">AR Collections Settings</h6>
			</div>
			<div class="card-body">
				<div v-if="settingsLoading" class="d-flex justify-content-center py-3">
					<div class="spinner-border spinner-border-sm" role="status"></div>
				</div>
				<div v-else>
					<div class="row">
						<div class="col-md-6">
							<div class="mb-4">
								<h6>Sandbox Mode</h6>
								<p class="text-muted mb-2">When enabled, all emails are redirected to sandbox email addresses instead of real clients. No Plesk actions (admin disable/enable, site disable/enable) will be executed.</p>
								<div class="form-check form-switch">
									<input class="form-check-input" type="checkbox" id="sandboxToggle" v-model="arSettings.sandbox" style="width: 3em; height: 1.5em;">
									<label class="form-check-label ms-2 mt-1" for="sandboxToggle">
										<span v-if="arSettings.sandbox" class="text-warning fw-bold">Sandbox ON - Safe mode</span>
										<span v-else class="text-danger fw-bold">Sandbox OFF - LIVE MODE - Real emails to clients</span>
									</label>
								</div>
							</div>

							<div class="mb-4" v-if="arSettings.sandbox">
								<h6>Sandbox Email Recipients</h6>
								<p class="text-muted mb-2">All AR emails will be sent to these addresses instead of real clients. Separate multiple emails with commas.</p>
								<input type="text" class="form-control" v-model="arSettings.sandbox_emails" placeholder="ivan@townweb.com, cristina@townweb.com" />
							</div>

							<div v-if="!arSettings.sandbox" class="alert alert-danger mb-4">
								<font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
								<strong> Warning:</strong> With sandbox OFF, the daily scheduled command will send real emails to real client email addresses and execute real Plesk actions. Only disable sandbox after Cristina has reviewed all email templates.
							</div>

							<button class="btn btn-primary" @click="saveArSettings" :disabled="savingSettings">
								<font-awesome-icon v-if="savingSettings" :icon="['fas', 'spinner']" class="fa-spin" />
								Save Settings
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
		</template>

		<!-- Edit Template Modal -->
		<div class="modal fade" id="arTemplateEditModal" tabindex="-1">
			<div class="modal-dialog modal-xl">
				<div class="modal-content" v-if="editingTemplate">
					<div class="modal-header">
						<h5 class="modal-title">Edit: {{ editingTemplate.name }}</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal"></button>
					</div>
					<div class="modal-body">
						<div class="mb-3">
							<label class="form-label">Subject</label>
							<input type="text" class="form-control" v-model="editingTemplate.subject" />
							<small class="text-muted">You can use variables: <code v-for="v in templateVariables" :key="'subj-'+v" class="me-1">{{ wrapVar(v) }}</code></small>
						</div>
						<div class="mb-3">
							<label class="form-label">Email Body (HTML)</label>
							<Editor v-model="editingTemplate.html_content" :init="editorConfig" />
						</div>
						<div class="mb-3">
							<label class="form-label">From Name</label>
							<input type="text" class="form-control" v-model="editingTemplate.from_name" />
						</div>
						<div class="mb-3">
							<label class="form-label">From Email</label>
							<input type="email" class="form-control" v-model="editingTemplate.from_email" />
						</div>
						<div class="mb-3">
							<label class="form-label">Status</label>
							<select class="form-select" v-model="editingTemplate.status">
								<option value="active">Active</option>
								<option value="draft">Draft</option>
								<option value="archived">Archived</option>
							</select>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
						<button type="button" class="btn btn-primary" @click="saveTemplate" :disabled="savingTemplate">
							<font-awesome-icon v-if="savingTemplate" :icon="['fas', 'spinner']" class="fa-spin" />
							Save Template
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Details Modal -->
		<div class="modal fade" id="arDetailsModal" tabindex="-1">
			<div class="modal-dialog modal-lg">
				<div class="modal-content" v-if="selectedEscalation">
					<div class="modal-header">
						<h5 class="modal-title">{{ selectedEscalation.client ? selectedEscalation.client.name : '' }} - Escalation Details</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal"></button>
					</div>
					<div class="modal-body">
						<div class="row mb-3">
							<div class="col-md-6">
								<p><strong>Invoice:</strong> #{{ selectedEscalation.triggering_invoice ? selectedEscalation.triggering_invoice.invoice_number : '' }}</p>
								<p><strong>Amount:</strong> {{ selectedEscalation.triggering_invoice ? '$' + formatAmount(selectedEscalation.triggering_invoice.amount) : '' }}</p>
								<p><strong>Due Date:</strong> {{ selectedEscalation.triggering_invoice ? formatDate(selectedEscalation.triggering_invoice.due_date) : '' }}</p>
							</div>
							<div class="col-md-6">
								<p><strong>Stage:</strong> {{ stageLabel(selectedEscalation.current_stage) }}</p>
								<p><strong>Created:</strong> {{ formatDate(selectedEscalation.created_at) }}</p>
								<p v-if="selectedEscalation.paused"><strong>Paused by:</strong> {{ selectedEscalation.paused_by }} - {{ selectedEscalation.pause_reason }}</p>
								<p v-if="selectedEscalation.admin_disabled_at"><strong>Admin disabled:</strong> {{ formatDate(selectedEscalation.admin_disabled_at) }}</p>
								<p v-if="selectedEscalation.completed_at"><strong>Completed:</strong> {{ formatDate(selectedEscalation.completed_at) }}</p>
							</div>
						</div>

						<h6>Action Log</h6>
						<table class="table table-sm" v-if="selectedEscalation.actions && selectedEscalation.actions.length">
							<thead>
								<tr>
									<th>Date</th>
									<th>Action</th>
									<th>By</th>
									<th>Notes</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="action in selectedEscalation.actions" :key="action.id">
									<td>{{ formatDateTime(action.created_at) }}</td>
									<td><span class="badge bg-secondary">{{ action.action_type }}</span></td>
									<td>{{ action.performed_by }}</td>
									<td>{{ action.notes || '-' }}</td>
								</tr>
							</tbody>
						</table>
						<p v-else class="text-muted">No actions yet.</p>
					</div>
				</div>
			</div>
		</div>

		<!-- Pause Modal -->
		<div class="modal fade" id="arPauseModal" tabindex="-1">
			<div class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Pause Escalation</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal"></button>
					</div>
					<div class="modal-body">
						<div class="mb-3">
							<label class="form-label">Reason</label>
							<select class="form-select" v-model="pauseForm.reason">
								<option value="">Select reason...</option>
								<option value="Partial payment received">Partial payment received</option>
								<option value="Payment plan agreed">Payment plan agreed</option>
								<option value="Dispute under review">Dispute under review</option>
								<option value="Budget cycle delay">Budget cycle delay</option>
								<option value="Other">Other</option>
							</select>
							<input v-if="pauseForm.reason === 'Other'" type="text" class="form-control mt-2" v-model="pauseForm.customReason" placeholder="Describe reason..." />
						</div>
						<div class="mb-3">
							<label class="form-label">Expected Resolution Date</label>
							<input type="date" class="form-control" v-model="pauseForm.expectedResolution" :min="tomorrow" />
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
						<button type="button" class="btn btn-warning" @click="submitPause" :disabled="!pauseFormValid">Pause Workflow</button>
					</div>
				</div>
			</div>
		</div>
		<!-- Confirm Payment Modal -->
		<div class="modal fade" id="arPaymentModal" tabindex="-1">
			<div class="modal-dialog">
				<div class="modal-content" v-if="paymentTarget">
					<div class="modal-header">
						<h5 class="modal-title">Confirm Payment Received</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal"></button>
					</div>
					<div class="modal-body">
						<p>This will close the escalation for <strong>{{ paymentTarget.client ? paymentTarget.client.name : '' }}</strong> and restore any disabled access.</p>
						<div v-if="paymentTarget.triggering_invoice" class="mb-3">
							<p class="mb-1"><strong>Invoice:</strong> #{{ paymentTarget.triggering_invoice.invoice_number }}</p>
							<p class="mb-1"><strong>Amount:</strong> ${{ formatAmount(paymentTarget.triggering_invoice.amount) }}</p>
						</div>
						<div class="mb-3">
							<label class="form-label">Notes (optional)</label>
							<input type="text" class="form-control" v-model="paymentNotes" placeholder="e.g. Check received, wire transfer confirmed..." />
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
						<button type="button" class="btn btn-success" @click="submitPayment">
							<font-awesome-icon :icon="['fas', 'dollar-sign']" /> Confirm Payment
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Disable Site Modal -->
		<div class="modal fade" id="arDisableSiteModal" tabindex="-1">
			<div class="modal-dialog">
				<div class="modal-content" v-if="disableSiteTarget">
					<div class="modal-header bg-danger text-white">
						<h5 class="modal-title">Disable Public Website</h5>
						<button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
					</div>
					<div class="modal-body">
						<p><strong>This will take down the public-facing website</strong> for <strong>{{ disableSiteTarget.client ? disableSiteTarget.client.name : '' }}</strong>.</p>
						<p>Residents will not be able to access the website. This action requires leadership approval and should only be used for extreme cases (90+ days overdue, no communication).</p>
						<div v-if="disableSiteTarget.triggering_invoice" class="mb-3">
							<p class="mb-1"><strong>Invoice:</strong> #{{ disableSiteTarget.triggering_invoice.invoice_number }}</p>
							<p class="mb-1"><strong>Amount:</strong> ${{ formatAmount(disableSiteTarget.triggering_invoice.amount) }}</p>
							<p class="mb-1"><strong>Days overdue:</strong> {{ daysOverdue(disableSiteTarget.triggering_invoice.due_date) }}</p>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
						<button type="button" class="btn btn-danger" @click="submitDisableSite">
							<font-awesome-icon :icon="['fas', 'lock']" /> Disable Public Site
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Resume Modal -->
		<div class="modal fade" id="arResumeModal" tabindex="-1">
			<div class="modal-dialog">
				<div class="modal-content" v-if="resumeTarget">
					<div class="modal-header">
						<h5 class="modal-title">Resume Escalation</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal"></button>
					</div>
					<div class="modal-body">
						<p>Resume the escalation workflow for <strong>{{ resumeTarget.client ? resumeTarget.client.name : '' }}</strong>.</p>
						<div v-if="resumeTarget.pause_reason" class="alert alert-warning mb-3">
							<strong>Paused because:</strong> {{ resumeTarget.pause_reason }}
							<span v-if="resumeTarget.paused_by"> (by {{ resumeTarget.paused_by }})</span>
						</div>
						<div class="mb-3">
							<label class="form-label">Reason for resuming</label>
							<select class="form-select" v-model="resumeForm.reason">
								<option value="">Select reason...</option>
								<option value="Payment plan not honored">Payment plan not honored</option>
								<option value="Dispute resolved, balance remains">Dispute resolved, balance remains</option>
								<option value="Budget cycle ended, still unpaid">Budget cycle ended, still unpaid</option>
								<option value="No response from client">No response from client</option>
								<option value="Partial payment applied, balance remains">Partial payment applied, balance remains</option>
								<option value="Other">Other</option>
							</select>
							<input v-if="resumeForm.reason === 'Other'" type="text" class="form-control mt-2" v-model="resumeForm.customReason" placeholder="Describe reason..." />
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
						<button type="button" class="btn btn-success" @click="submitResume" :disabled="!resumeFormValid">
							<font-awesome-icon :icon="['fas', 'play']" /> Resume Workflow
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import { mapState } from 'vuex'
import restApi from '../../api/index.js'
import { Modal } from 'bootstrap'
import moment from 'moment'
import Editor from '@tinymce/tinymce-vue'

export default {
	name: 'ArCollections',
	components: { PageHeader, Editor },

	data() {
		return {
			tableData: [],
			loading: false,
			pagination: {},
			page: 1,
			search: '',
			stageFilter: '',
			statusFilter: 'active',
			searchTimeout: null,
			selectedEscalation: null,
			pauseTarget: null,
			pauseForm: {
				reason: '',
				customReason: '',
				expectedResolution: '',
			},
			paymentTarget: null,
			paymentNotes: '',
			disableSiteTarget: null,
			resumeTarget: null,
			resumeForm: {
				reason: '',
				customReason: '',
			},
			activeTab: 'escalations',
			arSettings: { sandbox: true, sandbox_emails: '' },
			settingsLoading: false,
			savingSettings: false,
			emailTemplates: [],
			templatesLoading: false,
			editingTemplate: null,
			savingTemplate: false,
			templateVariables: ['firstName', 'invoiceNumber', 'amount', 'dueDate', 'paymentLink', 'websiteUrl'],
			editorConfig: {
				menubar: false,
				plugins: 'link code lists',
				toolbar: 'fontselect fontsizeselect formatselect | bold italic underline strikethrough | bullist numlist | alignleft aligncenter alignright | link removeformat code',
				height: 400,
			},
			stats: {
				active: 0,
				paused: 0,
				adminDisabled: 0,
				totalAmount: '0.00',
			},
		}
	},

	computed: {
		...mapState(['authUser']),
		tomorrow() {
			return moment().add(1, 'day').format('YYYY-MM-DD')
		},
		pauseFormValid() {
			const reason = this.pauseForm.reason === 'Other' ? this.pauseForm.customReason : this.pauseForm.reason
			return reason && this.pauseForm.expectedResolution
		},
		resumeFormValid() {
			const reason = this.resumeForm.reason === 'Other' ? this.resumeForm.customReason : this.resumeForm.reason
			return !!reason
		},
	},

	created() {
		this.getTableData()
		this.loadArSettings()
	},

	mounted() {
		this.detailsModal = new Modal('#arDetailsModal')
		this.pauseModal = new Modal('#arPauseModal')
		this.paymentModal = new Modal('#arPaymentModal')
		this.disableSiteModal = new Modal('#arDisableSiteModal')
		this.resumeModal = new Modal('#arResumeModal')
		this.templateEditModal = new Modal('#arTemplateEditModal')
	},

	methods: {
		getTableData() {
			this.loading = true
			let params = {
				page: this.page,
				per_page: 25,
			}

			if (this.search) params.search = this.search
			if (this.stageFilter) params.stage = this.stageFilter

			if (this.statusFilter === 'active') {
				params.active_only = true
			} else if (this.statusFilter === 'paused') {
				params.active_only = true
				// Will filter client-side for paused
			}

			restApi
				.get('/ar/escalations', { params })
				.then(({ data }) => {
					let items = data.data
					if (this.statusFilter === 'paused') {
						items = items.filter(e => e.paused)
					} else if (this.statusFilter === 'completed') {
						// Need to fetch without active_only
						params.active_only = false
						restApi.get('/ar/escalations', { params }).then(({ data: d2 }) => {
							this.tableData = d2.data.filter(e => e.completed_at)
							this.pagination = { ...d2, data: undefined }
							this.loading = false
							this.computeStats(d2.data)
						})
						return
					}

					this.tableData = items
					this.pagination = { ...data, data: undefined }
					this.loading = false
					this.computeStats(data.data)
				})
				.catch(() => {
					this.loading = false
				})
		},

		computeStats(allData) {
			this.stats.active = allData.filter(e => !e.completed_at).length
			this.stats.paused = allData.filter(e => e.paused && !e.completed_at).length
			this.stats.adminDisabled = allData.filter(e => e.admin_disabled_at && !e.admin_reactivated_at && !e.completed_at).length
			let total = 0
			allData.forEach(e => {
				if (!e.completed_at && e.triggering_invoice) {
					total += parseFloat(e.triggering_invoice.amount) || 0
				}
			})
			this.stats.totalAmount = total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
		},

		debounceSearch() {
			clearTimeout(this.searchTimeout)
			this.searchTimeout = setTimeout(() => {
				this.page = 1
				this.getTableData()
			}, 400)
		},

		resetFilters() {
			this.search = ''
			this.stageFilter = ''
			this.statusFilter = 'active'
			this.page = 1
			this.getTableData()
		},

		goToPage(p) {
			if (p < 1 || p > this.pagination.last_page) return
			this.page = p
			this.getTableData()
		},

		pauseEscalation(esc) {
			this.pauseTarget = esc
			this.pauseForm = { reason: '', customReason: '', expectedResolution: '' }
			this.pauseModal.show()
		},

		submitPause() {
			const reason = this.pauseForm.reason === 'Other' ? this.pauseForm.customReason : this.pauseForm.reason
			restApi
				.post(`/ar/escalations/${this.pauseTarget.id}/pause`, {
					reason: reason,
					expected_resolution: this.pauseForm.expectedResolution,
				})
				.then(() => {
					this.pauseModal.hide()
					this.getTableData()
				})
		},

		resumeEscalation(esc) {
			this.resumeTarget = esc
			this.resumeForm = { reason: '', customReason: '' }
			this.resumeModal.show()
		},

		submitResume() {
			const reason = this.resumeForm.reason === 'Other' ? this.resumeForm.customReason : this.resumeForm.reason
			restApi
				.post(`/ar/escalations/${this.resumeTarget.id}/resume`, {
					notes: reason,
				})
				.then(() => {
					this.resumeModal.hide()
					this.getTableData()
				})
		},

		confirmPayment(esc) {
			this.paymentTarget = esc
			this.paymentNotes = ''
			this.paymentModal.show()
		},

		submitPayment() {
			restApi
				.post(`/ar/escalations/${this.paymentTarget.id}/confirm-payment`, {
					notes: this.paymentNotes,
				})
				.then(() => {
					this.paymentModal.hide()
					this.getTableData()
				})
		},

		disableSite(esc) {
			this.disableSiteTarget = esc
			this.disableSiteModal.show()
		},

		submitDisableSite() {
			restApi
				.post(`/ar/escalations/${this.disableSiteTarget.id}/disable-site`)
				.then(() => {
					this.disableSiteModal.hide()
					this.getTableData()
				})
		},

		showDetails(esc) {
			this.selectedEscalation = esc
			this.detailsModal.show()
		},

		// Settings methods
		loadArSettings() {
			this.settingsLoading = true
			restApi
				.get('/ar/settings')
				.then(({ data }) => {
					this.arSettings = data
					this.settingsLoading = false
				})
				.catch(() => {
					this.settingsLoading = false
				})
		},

		saveArSettings() {
			this.savingSettings = true
			restApi
				.post('/ar/settings', {
					sandbox: this.arSettings.sandbox,
					sandbox_emails: this.arSettings.sandbox_emails,
				})
				.then(({ data }) => {
					this.arSettings = data
					this.savingSettings = false
				})
				.catch(() => {
					this.savingSettings = false
				})
		},

		// Email template methods
		loadTemplates() {
			if (this.emailTemplates.length > 0) return
			this.templatesLoading = true
			restApi
				.get('/email-templates', { params: { category: 'ar_collections' } })
				.then(({ data }) => {
					this.emailTemplates = (data.data || data).filter(t => t.category === 'ar_collections')
					this.templatesLoading = false
				})
				.catch(() => {
					this.templatesLoading = false
				})
		},

		editTemplate(tpl) {
			this.editingTemplate = { ...tpl }
			this.templateEditModal.show()
		},

		saveTemplate() {
			this.savingTemplate = true
			restApi
				.put(`/email-templates/${this.editingTemplate.id}`, {
					subject: this.editingTemplate.subject,
					html_content: this.editingTemplate.html_content,
					from_name: this.editingTemplate.from_name,
					from_email: this.editingTemplate.from_email,
					status: this.editingTemplate.status,
				})
				.then(({ data }) => {
					this.savingTemplate = false
					this.templateEditModal.hide()
					// Update the local list
					const idx = this.emailTemplates.findIndex(t => t.id === this.editingTemplate.id)
					if (idx !== -1) {
						this.emailTemplates.splice(idx, 1, data.data || data)
					}
					this.editingTemplate = null
				})
				.catch(() => {
					this.savingTemplate = false
				})
		},

		wrapVar(v) {
			return '{{' + v + '}}'
		},

		truncateHtml(html) {
			if (!html) return ''
			// Strip the wrapper, show just the body content
			const match = html.match(/<body[^>]*>([\s\S]*)<\/body>/i)
			return match ? match[1] : html
		},

		// Formatting helpers
		formatAmount(val) {
			return parseFloat(val || 0)
				.toFixed(2)
				.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
		},
		formatDate(d) {
			return d ? moment(d).format('MMM D, YYYY') : ''
		},
		formatDateTime(d) {
			return d ? moment(d).format('MMM D, YYYY h:mm A') : ''
		},
		daysOverdue(dueDate) {
			if (!dueDate) return '-'
			const days = moment().diff(moment(dueDate), 'days')
			if (days < 0) return `${Math.abs(days)} days left`
			if (days === 0) return 'Due today'
			return `${days} days`
		},
		overdueBadgeClass(dueDate) {
			const days = moment().diff(moment(dueDate), 'days')
			if (days <= 0) return 'text-success'
			if (days <= 10) return 'text-warning'
			return 'text-danger fw-bold'
		},
		stageLabel(stage) {
			const labels = {
				pre_due: 'Pre-Due (-7)',
				due_day: 'Due Day',
				overdue_1: '+1 Day',
				overdue_5: '+5 Days',
				overdue_10: '+10 Days',
				overdue_20: '+20 Days',
			}
			return labels[stage] || stage
		},
		stageBadgeClass(stage) {
			const classes = {
				pre_due: 'bg-info text-dark',
				due_day: 'bg-secondary',
				overdue_1: 'bg-warning text-dark',
				overdue_5: 'bg-warning text-dark',
				overdue_10: 'bg-orange',
				overdue_20: 'bg-danger',
			}
			return classes[stage] || 'bg-secondary'
		},
		rowClass(esc) {
			if (esc.completed_at) return 'table-success'
			if (esc.paused) return 'table-warning'
			if (esc.admin_disabled_at && !esc.admin_reactivated_at) return 'table-danger'
			return ''
		},
	},
}
</script>

<style scoped>
.bg-orange {
	background-color: #fd7e14;
	color: white;
}
.d-flex.gap-1 > * {
	margin-right: 4px;
}
.d-flex.gap-1 > *:last-child {
	margin-right: 0;
}
</style>
